// Lightweight document head manager for a Vue SPA (no SSR / vue-meta dependency).
// Sets <title>, meta description, canonical link and OpenGraph / Twitter tags
// at runtime, then restores the previous head state when the page unmounts.
//
// Usage (Options API):
//   import { applySeoMeta } from '@/composables/useSeoMeta';
//   mounted()      { this._seoCleanup = applySeoMeta({ ... }); }
//   beforeUnmount(){ this._seoCleanup && this._seoCleanup(); }

const SITE_NAME = 'أ/ مصطفى حسني - مدرس الكيمياء';
const DEFAULT_IMAGE = 'https://moustafahosney.com/favicon.ico';

function upsertMeta(attr, key, content, created) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
    created.push(el);
  }
  el.setAttribute('content', content);
  return el;
}

function upsertLink(rel, href, created) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
    created.push(el);
  }
  el.setAttribute('href', href);
  return el;
}

export function applySeoMeta(options = {}) {
  const {
    title,
    description = '',
    canonical = '',
    image = DEFAULT_IMAGE,
    type = 'website',
    locale = 'ar_EG',
  } = options;

  // Remember previous state so we can restore it on cleanup.
  const previousTitle = document.title;
  const created = []; // elements we add and must remove on cleanup
  const touched = []; // pre-existing elements we mutate: [el, attr, oldValue]

  const setExisting = (el, attr, value) => {
    if (!el) return;
    touched.push([el, attr, el.getAttribute(attr)]);
    el.setAttribute(attr, value);
  };

  if (title) document.title = title;

  // Standard meta description
  const descEl = document.head.querySelector('meta[name="description"]');
  if (descEl) setExisting(descEl, 'content', description);
  else upsertMeta('name', 'description', description, created);

  // Canonical URL
  if (canonical) {
    const existingCanonical = document.head.querySelector('link[rel="canonical"]');
    if (existingCanonical) setExisting(existingCanonical, 'href', canonical);
    else upsertLink('canonical', canonical, created);
  }

  // OpenGraph
  upsertMeta('property', 'og:title', title || previousTitle, created);
  upsertMeta('property', 'og:description', description, created);
  upsertMeta('property', 'og:type', type, created);
  upsertMeta('property', 'og:site_name', SITE_NAME, created);
  upsertMeta('property', 'og:locale', locale, created);
  upsertMeta('property', 'og:image', image, created);
  if (canonical) upsertMeta('property', 'og:url', canonical, created);

  // Twitter card
  upsertMeta('name', 'twitter:card', 'summary_large_image', created);
  upsertMeta('name', 'twitter:title', title || previousTitle, created);
  upsertMeta('name', 'twitter:description', description, created);
  upsertMeta('name', 'twitter:image', image, created);

  // Document language / direction for the crawler + accessibility
  const htmlEl = document.documentElement;
  const prevLang = htmlEl.getAttribute('lang');
  const prevDir = htmlEl.getAttribute('dir');
  htmlEl.setAttribute('lang', 'ar');
  htmlEl.setAttribute('dir', 'rtl');

  // Cleanup: remove what we created, restore what we touched.
  return function cleanup() {
    document.title = previousTitle;
    created.forEach((el) => el.parentNode && el.parentNode.removeChild(el));
    touched.forEach(([el, attr, oldValue]) => {
      if (oldValue === null) el.removeAttribute(attr);
      else el.setAttribute(attr, oldValue);
    });
    if (prevLang === null) htmlEl.removeAttribute('lang');
    else htmlEl.setAttribute('lang', prevLang);
    if (prevDir === null) htmlEl.removeAttribute('dir');
    else htmlEl.setAttribute('dir', prevDir);
  };
}

export default applySeoMeta;
